import { Component, signal, ViewChildren, QueryList, ElementRef, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterLink, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ApiService } from './api.service';

@Component({
  selector: 'app-otp',
  standalone: true,
  imports: [RouterLink, FormsModule, CommonModule],
  template: `
    <div class="min-h-screen flex font-sans" style="background:#f1f9fe;">
      <!-- Left decorative panel — Warely style brand panel -->
      <div class="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col items-center justify-center p-12"
           style="background:linear-gradient(145deg, #0C3A5A 0%, #1A6FA6 50%, #2B9AD2 100%);">
        <div class="absolute -top-20 -left-20 w-72 h-72 rounded-full opacity-10" style="background:#5EC9F0;"></div>
        <div class="absolute -bottom-32 -right-16 w-96 h-96 rounded-full opacity-10" style="background:#F6AF26;"></div>
        <div class="relative z-10 text-center">
          <div class="flex items-center justify-center mb-8">
            <img src="/logo-dark.png" alt="NZ Plumbing" class="h-16 w-auto" onerror="this.style.display='none';">
          </div>
          <h2 class="text-white text-3xl font-bold mb-4 leading-snug">Secure Admin Access</h2>
          <p class="text-white/70 text-base leading-relaxed max-w-sm mx-auto">We've sent a 6-digit verification code to your registered email to ensure account security.</p>
        </div>
      </div>

      <!-- Right OTP Panel -->
      <div class="flex-1 flex items-center justify-center p-8">
        <div class="w-full max-w-md relative">
          @if (resendSuccess()) {
            <div class="absolute -top-12 left-0 right-0 bg-emerald-500 text-white text-sm font-bold py-2.5 px-4 rounded-xl text-center shadow-lg animate-fade-in-down z-10">
              New OTP sent successfully!
            </div>
          }
          <div class="flex items-center justify-center mb-8 lg:hidden">
            <img src="/logo.png" alt="NZ Plumbing" class="h-14 w-auto">
          </div>

          <div class="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 relative" style="border:1px solid #EEF2F7;">
            <button routerLink="/login" class="absolute left-6 top-6 w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
              <i class="ti ti-arrow-left text-[20px]"></i>
            </button>

            <div class="text-center mt-6">
              <h1 class="text-2xl font-bold text-slate-900 mb-1">Verify Login</h1>
              <p class="text-slate-500 text-sm mb-8">Enter the 6-digit code sent to your email.</p>
            </div>

            <div class="space-y-6">
              <div class="flex justify-between gap-2">
                @for (digit of otpDigits; track $index) {
                  <input #otpInput type="text" autocomplete="one-time-code" inputmode="numeric" maxlength="6" [(ngModel)]="otpDigits[$index]" (input)="onInput($event, $index)" (keydown)="onKeyDown($event, $index)" (paste)="onPaste($event)" class="w-12 h-14 text-center text-2xl font-bold rounded-2xl border bg-slate-50 border-slate-200 focus:border-[#2B9AD2] focus:ring-2 focus:ring-[#2B9AD2]/10 outline-none transition-all" [disabled]="isLoading() || isResending()">
                }
              </div>
              @if (errorMsg()) {
                <div class="p-3 rounded-2xl flex items-center gap-2 text-sm font-medium" style="background:#FEF2F2; color:#EF4444;">
                  <i class="ti ti-alert-circle text-[18px]"></i> {{errorMsg()}}
                </div>
              }
              <button (click)="verifyOtp()" [disabled]="isLoading() || !isOtpComplete() || isResending()" class="flex items-center justify-center w-full h-12 text-white font-bold rounded-2xl shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
                @if (isLoading()) { <i class="ti ti-rotate animate-spin mr-2 text-[20px]"></i> Verifying... } @else { Verify &amp; Enter HQ }
              </button>
              <div class="text-center text-sm text-slate-500 mt-6">
                @if (resendTimer() > 0) { <p>Resend code in <span class="font-bold text-slate-900">{{resendTimer()}}s</span></p> } 
                @else { <p>Didn't receive code? <button (click)="resendOtp()" [disabled]="isResending()" class="text-[#2B9AD2] font-bold hover:underline ml-1">@if (isResending()) { Sending... } @else { Resend }</button></p> }
              </div>
            </div>
          </div>
          <p class="text-center text-xs text-slate-400 mt-6">© {{ year }} NZ Plumbing Limited. Partner access.</p>
        </div>
      </div>
    </div>
  `,
  styles: [`
    @keyframes fadeInDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
    .animate-fade-in-down { animation: fadeInDown 0.3s ease-out forwards; }
  `]
})
export class OtpComponent implements OnInit, OnDestroy {
  otpDigits = ['', '', '', '', '', ''];
  isLoading = signal(false);
  isResending = signal(false);
  resendSuccess = signal(false);
  errorMsg = signal('');
  resendTimer = signal(30);
  year = new Date().getFullYear();
  
  private timerInterval: ReturnType<typeof setInterval> | undefined;

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  private router = inject(Router);
  private api = inject(ApiService);

  ngOnInit() {
    if (!this.api.pendingEmail()) {
      this.router.navigate(['/login']);
      return;
    }
    this.startResendTimer();
  }

  ngOnDestroy() {
    this.clearTimer();
  }

  startResendTimer(seconds = 30) {
    this.resendTimer.set(seconds);
    this.clearTimer();
    this.timerInterval = setInterval(() => {
      if (this.resendTimer() > 0) { this.resendTimer.update(v => v - 1); } 
      else { this.clearTimer(); }
    }, 1000);
  }

  clearTimer() { if (this.timerInterval) clearInterval(this.timerInterval); }

  resendOtp() {
    if (!this.api.pendingEmail()) {
      this.router.navigate(['/login']);
      return;
    }

    this.isResending.set(true);
    this.errorMsg.set('');
    this.resendSuccess.set(false);

    this.api.resendOtp().subscribe({
      next: (res: any) => {
        this.isResending.set(false);
        if (res.success) {
          this.resendSuccess.set(true);
          this.otpDigits = ['', '', '', '', '', ''];
          this.startResendTimer();
          setTimeout(() => this.resendSuccess.set(false), 3000);
        } else {
          this.errorMsg.set(res.message || 'Could not send a new verification code.');
        }
      },
      error: (err) => {
        this.isResending.set(false);
        const retryAfter = Number(err?.error?.data?.retry_after || 0);
        if (retryAfter > 0) {
          this.startResendTimer(retryAfter);
        }
        this.errorMsg.set(err?.error?.message || 'Could not send a new verification code.');
      }
    });
  }

  onInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    // Handle iOS Keyboard Autofill dropping 6 digits into a single box
    if (value && value.length > 1) {
       const digits = value.replace(/\D/g, '').substring(0, 6);
       for (let i = 0; i < digits.length; i++) {
          this.otpDigits[i] = digits[i];
       }
       
       setTimeout(() => {
          this.otpInputs.last.nativeElement.focus();
          if (this.isOtpComplete()) this.verifyOtp();
       }, 50);
       return;
    }

    if (value && index < 5) this.otpInputs.toArray()[index + 1].nativeElement.focus();
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const digits = pastedData.replace(/\D/g, '').substring(0, 6);
    
    if (digits) {
      for (let i = 0; i < digits.length; i++) {
        this.otpDigits[i] = digits[i];
      }
      
      setTimeout(() => {
        const nextIndex = Math.min(digits.length, 5);
        this.otpInputs.toArray()[nextIndex].nativeElement.focus();
        if (this.isOtpComplete()) this.verifyOtp();
      }, 50);
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
      this.otpInputs.toArray()[index - 1].nativeElement.focus();
    }
  }

  isOtpComplete(): boolean { return this.otpDigits.every(d => d.length === 1); }

  verifyOtp() {
    if (!this.isOtpComplete()) return;
    
    this.isLoading.set(true);
    this.errorMsg.set('');
    const code = this.otpDigits.join('');
    
    this.api.verifyOtp(code).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.router.navigate(['/admin/dashboard']);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.errorMsg.set(err.error?.message || 'Invalid verification code. Please try again.');
        this.otpDigits = ['', '', '', '', '', ''];
        setTimeout(() => this.otpInputs.first.nativeElement.focus(), 100);
      }
    });
  }
}


